import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getIndicatifByCode, validatePhone } from '../config/membreFormConfig';
import { ROLE_CONFIG } from './profileConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useProfilePageModern({ userType }) {
  const { getAuthHeader, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('informations');
  const [editMode, setEditMode] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({});
  const [phoneData, setPhoneData] = useState({ indicatif: '+33', numero: '' });

  const roleConfig = ROLE_CONFIG[profile?.role] || ROLE_CONFIG[userType] || ROLE_CONFIG.member;

  useEffect(() => {
    fetchProfile();
  }, []);  // eslint-disable-line

  useEffect(() => {
    if (profile) {
      initFormData();
    }
  }, [profile]);  // eslint-disable-line

  const fetchProfile = async () => {
    try {
      let endpoint = `${BACKEND_URL}/api/members/me`;

      if (userType === 'cooper') {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/coopers/${user?.id}`, { headers: getAuthHeader() });
          setProfile(response.data);
          return;
        } catch {
          // Fallback vers l'endpoint générique
        }
      } else if (userType === 'expert') {
        endpoint = `${BACKEND_URL}/api/experts/me`;
      }

      if (userType === 'admin' || userType === 'super_admin') {
        if (user) {
          setProfile({
            ...user,
            role: user.role || userType,
            nom: user.nom || user.name?.split(' ').pop() || '',
            prenom: user.prenom || user.name?.split(' ').slice(0, -1).join(' ') || '',
            email: user.email || '',
            telephone: user.telephone || user.phone || '',
            date_inscription: user.date_inscription || user.created_at
          });
          return;
        }
      }

      const response = await axios.get(endpoint, { headers: getAuthHeader() });
      setProfile(response.data);
    } catch {
      if (user) {
        setProfile({
          ...user,
          role: user.role || userType,
          nom: user.nom || '',
          prenom: user.prenom || '',
          email: user.email || ''
        });
      } else {
        toast.error('Erreur lors du chargement du profil');
      }
    } finally {
      setLoading(false);
    }
  };

  const initFormData = () => {
    const data = {
      nom: profile.nom || '',
      prenom: profile.prenom || '',
      email: profile.email || '',
      telephone: profile.telephone || '',
      adresse: profile.adresse || '',
      pays: profile.pays || 'France',
      date_naissance: profile.date_naissance?.split('T')[0] || '',
      genre: profile.genre || '',
      photo_profil_url: profile.photo_profil_url || profile.logo_url || '',
      raison_sociale: profile.raison_sociale || profile.nom_entreprise || profile.entreprise || '',
      siret: profile.siret || '',
      statut_juridique: profile.statut_juridique || '',
      code_naf: profile.code_naf || '',
      secteur_activite: profile.secteur_activite || '',
      effectif: profile.effectif || '',
      profession: profile.profession || '',
      type_logement: profile.type_logement || '',
      nombre_foyer: profile.nombre_foyer || '',
      statut_emploi: profile.statut_emploi || '',
      activite_professionnelle: profile.activite_professionnelle || null,
      specialite: profile.specialite || '',
      experience_annees: profile.experience_annees || '',
      certifications: profile.certifications || ''
    };
    setFormData(data);

    if (profile.telephone) {
      const parts = profile.telephone.split(' ');
      if (parts.length > 1 && parts[0].startsWith('+')) {
        setPhoneData({ indicatif: parts[0], numero: parts.slice(1).join(' ') });
      } else {
        setPhoneData({ indicatif: '+33', numero: profile.telephone });
      }
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5 MB)');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload/profile-image`,
        formDataUpload,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );

      handleChange('photo_profil_url', response.data.url);
      toast.success('Photo mise à jour !');
    } catch {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const telephoneComplet = `${phoneData.indicatif} ${phoneData.numero}`;
      const dataToSend = { ...formData, telephone: telephoneComplet };

      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '' || dataToSend[key] === null) {
          delete dataToSend[key];
        }
      });

      let updateEndpoint = `${BACKEND_URL}/api/auth/profile`;
      if (userType === 'expert') {
        updateEndpoint = `${BACKEND_URL}/api/experts/me`;
      } else if (userType === 'member') {
        updateEndpoint = `${BACKEND_URL}/api/members/profile`;
      } else if (userType === 'cooper') {
        updateEndpoint = `${BACKEND_URL}/api/coopers/${user?.id}`;
      }

      const response = await axios.put(updateEndpoint, dataToSend, { headers: getAuthHeader() });

      setProfile(response.data);
      setEditMode(false);
      toast.success('✅ Profil mis à jour avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const selectedIndicatif = getIndicatifByCode(phoneData.indicatif);
  const phoneValidation = validatePhone(phoneData.numero, phoneData.indicatif);

  return {
    profile, loading, saving, activeTab, setActiveTab, editMode, setEditMode,
    uploadingPhoto, fileInputRef, formData, phoneData, setPhoneData,
    handleChange, handlePhotoUpload, handleSubmit, initFormData,
    selectedIndicatif, phoneValidation, roleConfig, getAuthHeader,
  };
}
